<script lang="ts">
export default {
  name: 'PromptManager',
};
</script>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import PromptPlaygroundPage from './PromptPlaygroundPage.vue';
import PromptMappingPage from './PromptMappingPage.vue';
import PromptManagerPage from './PromptManagerPage.vue';
import ResultsPage from './ResultsPage.vue';

const route = useRoute();
const router = useRouter();

// Valid tab values
const validTabs = ['playground', 'mapping', 'prompts', 'results'];

// Initialize currentTab from URL parameter or default to 'playground'
const getInitialTab = (): string => {
  const tabParam = route.query.tab as string;
  return validTabs.includes(tabParam) ? tabParam : 'playground';
};

const currentTab = ref<string>(getInitialTab());

// Params that belong to specific tabs
const tabParams: Record<string, string[]> = {
  prompts: ['prompt', 'version'],
  results: ['resultId'],
};

// Watch for tab changes and update URL, cleaning up stale params from previous tab
watch(currentTab, (newTab, oldTab) => {
  if (route.query.tab !== newTab) {
    const query = { ...route.query, tab: newTab };

    // Remove params that belong to the previous tab
    if (oldTab && tabParams[oldTab]) {
      for (const param of tabParams[oldTab]) {
        delete query[param];
      }
    }

    router.replace({ query });
  }
});

// Watch for URL parameter changes and update current tab
watch(() => route.query.tab, (newTab) => {
  if (typeof newTab === 'string' && validTabs.includes(newTab) && newTab !== currentTab.value) {
    currentTab.value = newTab;
  }
});

// Set initial tab from URL on mount
onMounted(() => {
  const tabFromUrl = route.query.tab as string;
  if (tabFromUrl && validTabs.includes(tabFromUrl)) {
    currentTab.value = tabFromUrl;
  }
});
</script>

<template>
  <div class="tw:min-h-full tw:bg-linear-to-br tw:from-slate-50 tw:to-blue-50 tw:p-6">
    <div class="tw:mx-auto tw:max-w-7xl tw:space-y-6">
      <!-- Navigation Tabs -->
      <TabsRoot v-model="currentTab" class="tw:w-full">
        <TabsList
          class="tw:inline-flex tw:h-10 tw:items-center tw:justify-center tw:rounded-md tw:bg-slate-100 tw:p-1 tw:text-slate-600">
          <TabsTrigger
            value="playground"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Playground
          </TabsTrigger>
          <TabsTrigger
            value="mapping"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Mapping
          </TabsTrigger>
          <TabsTrigger
            value="prompts"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Prompts
          </TabsTrigger>
          <TabsTrigger
            value="results"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Results
          </TabsTrigger>
        </TabsList>

        <!-- Playground Tab Content -->
        <TabsContent value="playground" class="tw:mt-4">
          <PromptPlaygroundPage />
        </TabsContent>

        <!-- Mapping Tab Content -->
        <TabsContent value="mapping" class="tw:mt-4">
          <PromptMappingPage />
        </TabsContent>

        <!-- Prompts Tab Content -->
        <TabsContent value="prompts" class="tw:mt-4">
          <PromptManagerPage />
        </TabsContent>

        <!-- Results Tab Content -->
        <TabsContent value="results" class="tw:mt-4">
          <ResultsPage />
        </TabsContent>
      </TabsRoot>
    </div>
  </div>
</template>
